/**
 * The constraints step (PIVOT-232) — `GET`/`PUT /api/onboarding/constraints`.
 *
 * Sits between the interview and finalize, and is a deterministic form on
 * purpose: the interview is seven turns of direction and values, and both
 * bake-off interviews spent every one of them there — leaving `locations` empty,
 * no commute anchor, and no salary or contract at all, so a third of the matches
 * shown were outside the candidate's own stated area. Asking "where can you
 * work, from where, for how little, on what contract, in which languages" needs
 * no model, so this step makes no model call and shows no `Thinking`.
 *
 * Drives two endpoints of one resource — the saved answers — so it owns that
 * data end to end and fetches its own seed (CLAUDE.md §2), the same way
 * `CvUpload` and `ConsentGate` each own one endpoint. The seed is what makes a
 * reload mid-step cost nothing.
 *
 * Every question offers an explicit "this does not limit me" box. A blank answer
 * is refused (here and by the server), because an empty `locations` list used to
 * mean both "we never asked" and "anywhere is fine" and nothing downstream could
 * tell those apart.
 */

import { useEffect, useState } from 'react'
import { getConstraints, saveConstraints } from '../api/onboarding'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import { CONTRACT_TYPES, LEVELS, PROVINCES, splitLocations } from '../lib/constraints'
import type { ConstraintAnswers } from '../types/domain'
import { Icon } from './Icon'
import '../styles/step-form.css'
import '../styles/location-chips.css'
import './ConstraintsStep.css'

interface ConstraintsStepProps {
  /** Called once the answers are stored server-side; the page finalizes next. */
  onSaved: () => void
}

const LANGUAGES: { value: string; labelKey: TranslationKey }[] = [
  { value: 'french', labelKey: 'onboarding.constraintsLanguageFrench' },
  { value: 'dutch', labelKey: 'onboarding.constraintsLanguageDutch' },
  { value: 'english', labelKey: 'onboarding.constraintsLanguageEnglish' },
]

/** What the form holds while it is being filled in, before it becomes a body. */
interface FormState {
  /** Ticked province codes -- the new picker (PIVOT-352). */
  locationCodes: string[]
  /** Free-text strings from a profile answered before this ticket. Shown as
   * removable chips only: there is no control that adds to this list. */
  legacyLocations: string[]
  locationsAnywhere: boolean
  anchor: string
  anchorNone: boolean
  contracts: string[]
  contractsAny: boolean
  salary: string
  salaryNoFloor: boolean
  levels: Record<string, string>
  languagesNone: boolean
}

const EMPTY: FormState = {
  locationCodes: [],
  legacyLocations: [],
  locationsAnywhere: false,
  anchor: '',
  anchorNone: false,
  contracts: [],
  contractsAny: false,
  salary: '',
  salaryNoFloor: false,
  levels: {},
  languagesNone: false,
}

/** Re-open the form on what was saved, so a reload costs no retyping. */
function seedFrom(answers: ConstraintAnswers): FormState {
  const levels: Record<string, string> = {}
  for (const entry of answers.languages) {
    const match = /^\s*([^(]+?)\s*\(([^)]+)\)\s*$/.exec(entry)
    if (match) levels[match[1].toLowerCase()] = match[2].toLowerCase()
  }
  // A4: a profile answered before this ticket carries only free-text town
  // names here; one answered on the new grid carries only province codes; a
  // profile mid-migration can carry both, and both render.
  const { provinceCodes, legacy } = splitLocations(answers.locations)
  return {
    locationCodes: provinceCodes,
    legacyLocations: legacy,
    locationsAnywhere: answers.locations_anywhere,
    anchor: answers.commute_anchor,
    anchorNone: answers.commute_anchor_none,
    contracts: answers.contract_types,
    contractsAny: answers.contract_types_any,
    salary: answers.salary_min === null ? '' : String(answers.salary_min),
    salaryNoFloor: answers.salary_no_floor,
    levels,
    languagesNone: answers.languages_none,
  }
}

function toAnswers(form: FormState): ConstraintAnswers {
  const stated = LANGUAGES.filter(({ value }) => (form.levels[value] ?? '') !== '')
  const salary = Number.parseInt(form.salary, 10)
  return {
    locations: form.locationsAnywhere ? [] : [...form.locationCodes, ...form.legacyLocations],
    locations_anywhere: form.locationsAnywhere,
    commute_anchor: form.anchorNone ? '' : form.anchor.trim(),
    commute_anchor_none: form.anchorNone,
    contract_types: form.contractsAny ? [] : form.contracts,
    contract_types_any: form.contractsAny,
    salary_min: form.salaryNoFloor || Number.isNaN(salary) ? null : salary,
    salary_no_floor: form.salaryNoFloor,
    languages: form.languagesNone
      ? []
      : stated.map(({ value }) => `${value} (${form.levels[value]})`),
    languages_none: form.languagesNone,
  }
}

/**
 * Whether every constraint carries a value or its no-constraint marker — the
 * same rule the server enforces, checked here so the refusal is a sentence
 * rather than a 422.
 *
 * PIVOT-350: each of the three language selects also has to be off its
 * placeholder — "I never chose" is no longer a value `toAnswers` can send —
 * unless the whole question is opted out of via `languagesNone`. A candidate
 * who says nothing about Dutch used to keep every `dutch_required` posting;
 * forcing an explicit choice per language (`none` included) is the fix.
 */
function isComplete(answers: ConstraintAnswers, form: FormState): boolean {
  const everyLanguageAnswered =
    form.languagesNone || LANGUAGES.every((language) => (form.levels[language.value] ?? '') !== '')
  return (
    answers.locations.length > 0 !== answers.locations_anywhere &&
    answers.commute_anchor.length > 0 !== answers.commute_anchor_none &&
    answers.contract_types.length > 0 !== answers.contract_types_any &&
    (answers.salary_min !== null) !== answers.salary_no_floor &&
    answers.languages.length > 0 !== answers.languages_none &&
    everyLanguageAnswered
  )
}

export function ConstraintsStep({ onSaved }: ConstraintsStepProps) {
  const { t } = useI18n()
  const saved = useApiResource(getConstraints)
  const [form, setForm] = useState<FormState | null>(null)
  const [error, setError] = useState<TranslationKey | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // One-way, like `Onboarding`'s own entry effect: the seed decides what the
    // form OPENS on, and after that the candidate's typing owns it. Guarding on
    // `form === null` keeps a late answer from wiping what they have typed.
    if (form !== null || saved.loading) return
    setForm(saved.data?.answers ? seedFrom(saved.data.answers) : EMPTY)
  }, [saved.loading, saved.data, form])

  if (form === null) {
    // An ordinary indexed read, so the plain loading line rather than
    // `Thinking`, which is reserved for waits on a model (CLAUDE.md §9).
    // `output` rather than `<p role="status">`: same live region, and it keeps
    // this file off the `prefer-tag-over-role` allowlist in `.oxlintrc.json`.
    return <output>{t('common.loading')}</output>
  }

  const patch = (change: Partial<FormState>) => setForm({ ...form, ...change })

  const submit = async () => {
    const answers = toAnswers(form)
    if (!isComplete(answers, form)) {
      setError('onboarding.constraintsIncomplete')
      return
    }
    setError(null)
    setSaving(true)
    try {
      await saveConstraints(answers)
    } catch {
      // Stay on the step with an honest error. Advancing would finalize a
      // profile whose constraints were never recorded — the exact silent
      // `locations=[]` this step exists to prevent.
      setError('onboarding.constraintsSaveFailed')
      setSaving(false)
      return
    }
    setSaving(false)
    onSaved()
  }

  return (
    <div className="stack">
      <h2>{t('onboarding.constraintsTitle')}</h2>
      <p>{t('onboarding.constraintsBody')}</p>

      <div className="step-form">
        <fieldset className="step-form-field">
          <legend className="step-form-label">
            {t('onboarding.constraintsLocationsLabel')}
          </legend>
          <div className="step-form-choices">
            {PROVINCES.map((province) => (
              <label key={province.value} className="step-form-choice">
                <input
                  type="checkbox"
                  checked={form.locationCodes.includes(province.value)}
                  disabled={form.locationsAnywhere}
                  onChange={(event) =>
                    patch({
                      locationCodes: event.target.checked
                        ? [...form.locationCodes, province.value]
                        : form.locationCodes.filter((value) => value !== province.value),
                    })
                  }
                />
                {t(province.labelKey)}
              </label>
            ))}
          </div>
          {/* A4: a legacy free-text value from a profile answered before this
              ticket -- removable only, since there is no control left that
              writes a new one of these. */}
          {form.legacyLocations.length > 0 && (
            <ul className="location-chip-list">
              {form.legacyLocations.map((location) => (
                <li key={location} className="location-chip">
                  {location}
                  <button
                    type="button"
                    className="location-chip-remove"
                    disabled={form.locationsAnywhere}
                    aria-label={t('onboarding.locationChipRemove', { location })}
                    onClick={() =>
                      patch({
                        legacyLocations: form.legacyLocations.filter((v) => v !== location),
                      })
                    }
                  >
                    <Icon name="cross" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="step-form-hint">{t('onboarding.constraintsLocationsHint')}</p>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.locationsAnywhere}
              onChange={(event) => patch({ locationsAnywhere: event.target.checked })}
            />
            {t('onboarding.constraintsLocationsAnywhere')}
          </label>
        </fieldset>

        <div className="step-form-field">
          <label htmlFor="constraints-anchor" className="step-form-label">
            {t('onboarding.constraintsAnchorLabel')}
          </label>
          <input
            id="constraints-anchor"
            type="text"
            value={form.anchor}
            disabled={form.anchorNone}
            onChange={(event) => patch({ anchor: event.target.value })}
          />
          <p className="step-form-hint">{t('onboarding.constraintsAnchorHint')}</p>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.anchorNone}
              onChange={(event) => patch({ anchorNone: event.target.checked })}
            />
            {t('onboarding.constraintsAnchorNone')}
          </label>
        </div>

        <fieldset className="step-form-field">
          <legend className="step-form-label">
            {t('onboarding.constraintsContractLabel')}
          </legend>
          <div className="step-form-choices">
            {CONTRACT_TYPES.map((contract) => (
              <label key={contract.value} className="step-form-choice">
                <input
                  type="checkbox"
                  checked={form.contracts.includes(contract.value)}
                  disabled={form.contractsAny}
                  onChange={(event) =>
                    patch({
                      contracts: event.target.checked
                        ? [...form.contracts, contract.value]
                        : form.contracts.filter((value) => value !== contract.value),
                    })
                  }
                />
                {t(contract.labelKey)}
              </label>
            ))}
          </div>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.contractsAny}
              onChange={(event) => patch({ contractsAny: event.target.checked })}
            />
            {t('onboarding.constraintsContractAny')}
          </label>
        </fieldset>

        <div className="step-form-field">
          <label htmlFor="constraints-salary" className="step-form-label">
            {t('onboarding.constraintsSalaryLabel')}
          </label>
          <input
            id="constraints-salary"
            type="number"
            min={0}
            step={100}
            value={form.salary}
            disabled={form.salaryNoFloor}
            onChange={(event) => patch({ salary: event.target.value })}
          />
          <p className="step-form-hint">{t('onboarding.constraintsSalaryHint')}</p>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.salaryNoFloor}
              onChange={(event) => patch({ salaryNoFloor: event.target.checked })}
            />
            {t('onboarding.constraintsSalaryNoFloor')}
          </label>
        </div>

        <fieldset className="step-form-field">
          <legend className="step-form-label">
            {t('onboarding.constraintsLanguagesLabel')}
          </legend>
          <p className="step-form-hint">{t('onboarding.constraintsLanguagesHint')}</p>
          <div className="step-form-levels">
            {LANGUAGES.map((language) => (
              <label key={language.value} className="step-form-level">
                {t(language.labelKey)}
                <select
                  value={form.levels[language.value] ?? ''}
                  disabled={form.languagesNone}
                  onChange={(event) =>
                    patch({ levels: { ...form.levels, [language.value]: event.target.value } })
                  }
                >
                  {LEVELS.map((level) => (
                    <option key={level.value} value={level.value} disabled={level.value === ''}>
                      {t(level.labelKey)}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.languagesNone}
              onChange={(event) => patch({ languagesNone: event.target.checked })}
            />
            {t('onboarding.constraintsLanguagesNone')}
          </label>
        </fieldset>
      </div>

      {error && (
        <p className="form-error" role="alert">
          {t(error)}
        </p>
      )}

      <button
        type="button"
        className="btn btn-primary"
        aria-busy={saving}
        disabled={saving}
        onClick={() => void submit()}
      >
        {t('onboarding.constraintsSubmit')}
      </button>
    </div>
  )
}
